import jwtService from "@service/jwt-service";
import { cloneDeep } from "lodash-es";
import { commonNotify } from "@utils/common";
import { CommonConst } from "@constants/common-const";

const axios = jwtService.axiosIns;

// 간편견적 요소 목록 조회
async function getHCostFactors() {
  return await axios.get("/hcosts/factors");
}

// 간편견적 요소 목록 수정
async function updateHCostFactors(...args: any[]) {
  return axios.put("/hcosts/factors", ...args);
}

/**
 * 간편견적 요소 목록 저장하기
 */
export async function saveHCostFactorList(updatehcostFactors: HcostFactor[]) {
  // 통신
  try {
    await updateHCostFactors(updatehcostFactors, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    commonNotify(CommonConst.completeSave);
  } catch (e) {
    commonNotify(CommonConst.errorSave, "error");
  } finally {
  }
}

/**
 * 간편견적 요소 목록 조회하기
 * @param hcostObejct 모델, 결과를 넘겨받은 경우, 요소별 선택 값을 채워줌
 * @param setHcostObject 객체에 요소값 할당
 * @param setHcostFactor
 */
export async function getHCostFactorList(
  hcostObejct: any = null,
  setHcostObject: any = null,
  setHcostFactor: any = null,
) {
  let hcostFactors: HcostFactor[] = [];

  try {
    // 조회
    let { data } = await getHCostFactors();

    hcostFactors = data;

    // 모델을 넘겨받은 경우, 초기값을 채워줌
    if (hcostObejct) {
      hcostFactors.map((factor, index) => {
        initSelectedValues(hcostObejct, factor);
        setSelectedCost(factor, hcostObejct.basicAreaPyeong, hcostObejct.basicLevel);
        return factor;
      });

      setHcostObject((draft: any) => {
        return {
          ...draft,
          ...hcostObejct,
          hcostFactors,
        };
      });
      if (setHcostFactor) {
        setHcostFactor(cloneDeep(hcostFactors));
      }
    }
    return hcostFactors;
  } catch (e) {
    console.log(e);
    commonNotify(CommonConst.errorGet, "error");
    throw e;
  } finally {
  }
}

/**
 * 총 금액(평균) 계산 및 저장
 */
export function calculateTotalCostAvg(
  hcostFactors: HcostFactor[],
  basicAreaPyeong: number,
  basicLevel: number,
): number {
  let totalCost: number = 0;
  hcostFactors.forEach(factor => {
    if (factor.selectedNode && factor.selectedScopeKey) {
      factor.factorScopes.forEach(scope => {
        if (factor.selectedScopeKey === scope.scopeKey) {
          setSelectedCost(factor, basicAreaPyeong, basicLevel);
          totalCost += factor.selectedCostAvg;
        }
      });
    }
  });
  return totalCost;
}

/**
 * 총 금액(최저) 계산 및 저장
 */
export function calculateTotalCostMin(
  hcostFactors: HcostFactor[],
  basicAreaPyeong: number,
  basicLevel: number,
): number {
  let totalCost: number = 0;
  hcostFactors.forEach(factor => {
    if (factor.selectedNode && factor.selectedScopeKey) {
      factor.factorScopes.forEach(scope => {
        if (factor.selectedScopeKey === scope.scopeKey) {
          setSelectedCost(factor, basicAreaPyeong, basicLevel);
          totalCost += factor.selectedCostMin;
        }
      });
    }
  });
  return totalCost;
}

/**
 * 총 금액(최대) 계산 및 저장
 */
export function calculateTotalCostMax(
  hcostFactors: HcostFactor[],
  basicAreaPyeong: number,
  basicLevel: number,
): number {
  let totalCost: number = 0;
  hcostFactors.forEach(factor => {
    if (factor.selectedNode && factor.selectedScopeKey) {
      factor.factorScopes.forEach(scope => {
        if (factor.selectedScopeKey === scope.scopeKey) {
          setSelectedCost(factor, basicAreaPyeong, basicLevel);
          totalCost += factor.selectedCostMax;
        }
      });
    }
  });
  return totalCost;
}

function initSelectedValues(object: any, factor: HcostFactor) {
  // 요소 선택 값 <- 선택된 모델요소
  factor.selectedNode = false;
  factor.selectedCostAvg = 0;
  factor.selectedCostMax = 0;
  factor.selectedCostMin = 0;
  factor.selectedScopeKey = null;
  if (object.modelFactors) {
    // 온실모델인 경우
    for (const modelFactor of object.modelFactors) {
      if (modelFactor.factor.id === factor.id) {
        factor.selectedNode = true;
        factor.selectedScopeKey = modelFactor.factorScope.scopeKey;
        factor.selectedCostAvg = calculateCostAvg(modelFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        factor.selectedCostMin = calculateCostMin(modelFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        factor.selectedCostMax = calculateCostMax(modelFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        return factor;
      }
    }
  } else if (object.resultFactors) {
    // 간편견적 결과인 경우
    for (const resultFactor of object.resultFactors) {
      if (resultFactor.factor.id === factor.id) {
        factor.selectedNode = true;
        factor.selectedScopeKey = resultFactor.factorScope.scopeKey;
        factor.selectedNode = factor.factorCat1 === "필수 공사";
        factor.selectedScopeKey = factor.factorCat1 === "필수 공사" ? resultFactor.factorScope.scopeKey : null;
        factor.selectedCostAvg = calculateCostAvg(resultFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        factor.selectedCostMin = calculateCostMin(resultFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        factor.selectedCostMax = calculateCostMax(resultFactor.factorScope, object.basicAreaPyeong, object.basicLevel);
        return factor;
      }
    }
  }
}

/**
 * 견적요소에 선택 금액 넣기
 */
function setSelectedCost(factor: HcostFactor, areaPyeong: number, level: number) {
  for (const factorScope of factor.factorScopes) {
    if (factorScope.scopeKey === factor.selectedScopeKey) {
      factor.selectedCostAvg = calculateCostAvg(factorScope, areaPyeong, level);
      factor.selectedCostMin = calculateCostMin(factorScope, areaPyeong, level);
      factor.selectedCostMax = calculateCostMax(factorScope, areaPyeong, level);
    }
  }
}

/**
 * 금액(평균) 계산하기
 * @param factorScope
 * @param areaPyeong
 * @param level
 * @return 금액(평균)
 */
export function calculateCostAvg(factorScope: HcostFactorScope, areaPyeong: number, level: number): number {
  let cost: number = 0;
  // 중수 없음 또는 1중
  cost = calculateCostPerAreaPyeong(factorScope.unitCost, factorScope.unitCostPerValue, areaPyeong);
  // 2중 이상
  if (level >= 2) {
    cost += calculateCostPerAreaPyeong(factorScope.unitCost2, factorScope.unitCostPerValue, areaPyeong);
  }
  // 3중 이상
  if (level >= 3) {
    cost += calculateCostPerAreaPyeong(factorScope.unitCost3, factorScope.unitCostPerValue, areaPyeong);
  }
  // 4중 이상
  if (level >= 4) {
    cost += calculateCostPerAreaPyeong(factorScope.unitCost4, factorScope.unitCostPerValue, areaPyeong);
  }
  return cost;
}

/**
 * 금액(최소) 계산하기
 * @param factorScope
 * @param areaPyeong
 * @param level
 * @return 금액(최소)
 */
export function calculateCostMin(factorScope: HcostFactorScope, areaPyeong: number, level: number): number {
  let cost: number = 0;
  cost = calculateCostAvg(factorScope, areaPyeong, level);
  return calculateMin(cost, factorScope.unitCostRange);
}

/**
 * 금액(최대) 계산하기
 * @param factorScope
 * @param areaPyeong
 * @param level
 * @return 금액(최대)
 */
export function calculateCostMax(factorScope: HcostFactorScope, areaPyeong: number, level: number): number {
  let cost: number = 0;
  cost = calculateCostAvg(factorScope, areaPyeong, level);
  return calculateMax(cost, factorScope.unitCostRange);
}

/**
 * 평당 금액 계산
 * @param unitCost 단가
 * @param unitCostPerValue 단가 기준값
 * @param areaPyeong 평수
 * @return 금액
 */
function calculateCostPerAreaPyeong(unitCost: number, unitCostPerValue: number, areaPyeong: number): number {
  if (unitCost === null || unitCostPerValue === null || areaPyeong === null) {
    return 0;
  }
  // 단가 기준값이 0이면 단가 기준값을 평수로 바꿔서, 개당 비용이 되도록함
  if (unitCostPerValue === 0) {
    unitCostPerValue = areaPyeong;
  }
  // 단가 * 올림(평수 / 단가 기준값)
  return unitCost * Math.ceil(areaPyeong / unitCostPerValue);
}

/**
 * 최소 금액 계산
 * @param cost 금액
 * @param unitCostRange 단가 범위
 * @return 최소 금액
 */
function calculateMin(cost: number, unitCostRange: number): number {
  if (cost === null) {
    return 0;
  }
  if (unitCostRange === null) {
    unitCostRange = 0;
  }
  // 금액 - 반올림(금액 * 단가범위)
  return cost - Math.round(cost * unitCostRange);
}

/**
 * 최대 금액 계산
 * @param cost 금액
 * @param unitCostRange 단가 범위
 * @return 최대 금액
 */
function calculateMax(cost: number, unitCostRange: number): number {
  if (cost === null) {
    return 0;
  }
  if (unitCostRange === null) {
    unitCostRange = 0;
  }
  // 금액 - 반올림(금액 * 단가범위)
  return cost + Math.round(cost * unitCostRange);
}
